import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordInputSchema, PRODUCT_NAME, type ForgotPasswordInput } from "@repo/shared";
import { Trans, useTranslation } from "react-i18next";
import { ArrowLeft, Mail } from "lucide-react";

import { AuthForm, AuthPanel, Button, Field, Input } from "@h2j-data-team/ui";

import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/_auth/forgot-password")({
  head: () => ({ meta: [{ title: `Forgot password — ${PRODUCT_NAME}` }] }),
  component: ForgotPasswordPage,
});

const loginSearch = {
  mode: undefined,
  token: undefined,
  error: undefined,
  redirect: undefined,
  email: undefined,
};

function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const [sentTo, setSentTo] = useState<string>();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordInputSchema),
  });

  const onSubmit = async (data: ForgotPasswordInput) => {
    const result = await authClient.requestPasswordReset({
      email: data.email,
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (result.error) {
      setError("root", {
        message: result.error.message ?? t("forgotPassword.failed"),
      });
      return;
    }

    setSentTo(data.email);
  };

  if (sentTo) {
    // Same text whether or not the account exists: the page never reveals who has one.
    return (
      <AuthPanel
        icon={<Mail />}
        title={t("forgotPassword.checkEmail.heading")}
        description={
          <Trans
            i18nKey="forgotPassword.checkEmail.description"
            ns="auth"
            values={{ email: sentTo }}
            components={{ strong: <strong /> }}
          />
        }
        footer={
          <Link to="/login" search={loginSearch}>
            {t("backToSignIn")}
          </Link>
        }
      />
    );
  }

  return (
    <AuthForm
      noValidate
      back={
        <Button variant="ghost" size="sm" asChild>
          <Link to="/login" search={loginSearch}>
            <ArrowLeft />
            {t("backToSignIn")}
          </Link>
        </Button>
      }
      title={t("forgotPassword.heading")}
      description={t("forgotPassword.description")}
      error={errors.root?.message}
      busy={isSubmitting}
      submitLabel={t("forgotPassword.submit")}
      submittingLabel={t("forgotPassword.sending")}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Field id="email" label={t("emailLabel")} error={errors.email?.message}>
        <Input
          type="email"
          autoComplete="email"
          placeholder={t("emailPlaceholder")}
          {...register("email")}
        />
      </Field>
    </AuthForm>
  );
}
